<template>
  <div>
    <demandeEntete
      :demandeur="demandeur"
      :typeDemande="typeDemande"
      :codeTypeDemande="codeTypeDemande"
      :dateCreation="dateCreation"
      :commentaire="commentaire"
      :complements="complements"
      :motifRefus="motifRefus"
    />

    <entiteModificationModal />

    <v-card flat>
      <v-list-item>
        <v-list-item-content two-line class="pt-0">
          <v-text-field
            dense
            filled
            v-model="form.origineDemande"
            :label="$t('subscribe.detail.form.origineDemande')"
            readonly
            hide-details="auto"
          />
        </v-list-item-content>
      </v-list-item>

      <v-list-item>
        <v-list-item-content two-line class="pt-0">
          <v-list-item-title class="headline">
            Personne morale</v-list-item-title
          >
          <v-list-item-subtitle>{{
            $t('personneMorale.detail.title.sub')
          }}</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>

      <v-form v-model="valid" lazy-validation ref="form">
        <v-card-text class="pt-0">
          <v-row dense>
            <v-col cols="12" md="6" sm="12">
              <v-text-field
                dense
                filled
                v-model="form.organisme"
                :rules="[obligatoireRule(), serveurRule('organisme')]"
                :label="$t('subscribe.detail.form.organisme')"
                readonly
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="6" sm="12">
              <adressesList
                v-model="form.adresses"
                :readonly="control.readonly"
                itemPath="adresses"
                :showHeader="false"
                :analyseDiff="analyseDiff"
                @annulerSuppression="annulerSuppression($event)"
              />
            </v-col>
            <v-col cols="12" md="12" sm="12">
              <v-btn
                v-if="!control.readonly"
                text
                small
                outlined
                color="secondary"
                @click="selectPM"
                >{{ $t('subscribe.detail.form.pm.add') }}
              </v-btn>
              <v-btn
                v-if="!control.readonly"
                text
                small
                outlined
                color="secondary"
                @click="createPM"
                >{{ $t('subscribe.detail.form.pm.new') }}
              </v-btn>
              <dialogRecherchePersonneMorale
                ref="dialogRecherchePM"
                :subtitle="$t('subscribe.detail.form.pm.sub')"
                @input="addPM"
              />
              <dialogCreationPersonneMorale
                ref="dialogCreationPM"
                :enDemandeCreation="false"
                :adressesSaisies="form.adresses"
                @input="addPM"
              />
            </v-col>

            <v-col cols="12" md="2">
              <v-text-field
                dense
                filled
                v-model="form.pmId"
                :label="$t('personneMorale.detail.form.id')"
                :rules="[obligatoireRule()]"
                readonly
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="5" sm="12">
              <v-text-field
                dense
                filled
                v-model="form.nomUsuel"
                :rules="[obligatoireRule(), serveurRule('nomUsuel')]"
                :label="$t('personneMorale.detail.form.nomUsuel')"
                readonly
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="5" sm="12">
              <v-text-field
                dense
                filled
                v-model="form.raisonSociale"
                :label="$t('personneMorale.detail.form.raisonSociale')"
                readonly
                hide-details="auto"
                :rules="[obligatoireRule(), serveurRule('raisonSociale')]"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-form>

      <v-list-item>
        <v-list-item-content two-line class="pt-0">
          <v-list-item-title class="headline">
            Personne physique</v-list-item-title
          >
          <v-list-item-subtitle
            >Informations sur la personne physique</v-list-item-subtitle
          >
        </v-list-item-content>
      </v-list-item>

      <v-form v-model="valid" lazy-validation ref="form">
        <v-card-text class="pt-0">
          <v-row dense>
            <v-col cols="12" md="4">
              <v-select
                return-object
                dense
                filled
                :items="listeCivilites"
                v-model="form.civilite"
                item-text="libelle"
                :label="$t('personnePhysique.detail.form.civilite')"
                :rules="[obligatoireRule(), serveurRule('civilite')]"
                hide-details="auto"
              >
                <template #selection="{item}">
                  {{ item.libelle }}
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                dense
                filled
                v-model="form.nom"
                :rules="[obligatoireRule(), serveurRule('nom')]"
                :label="$t('personnePhysique.detail.form.nom')"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                dense
                filled
                v-model="form.prenoms"
                :rules="[obligatoireRule(), serveurRule('prenoms')]"
                :label="$t('personnePhysique.detail.form.prenoms')"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="6" sm="12">
              <v-text-field
                dense
                filled
                v-model="form.telephone"
                :rules="[obligatoireRule(), serveurRule('telephone')]"
                :label="$t('personnePhysique.detail.form.telephone')"
                :readonly="control.readonly"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="6" sm="12">
              <v-text-field
                dense
                filled
                v-model="form.mailPrincipal"
                :rules="[obligatoireRule(), serveurRule('mailPrincipal')]"
                :label="$t('personnePhysique.detail.form.mailPrincipal')"
                :readonly="control.readonly"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                return-object
                dense
                filled
                :items="listeFonctions"
                v-model="form.contact.fonction"
                item-text="libelle"
                :label="$t('personnePhysique.detail.form.contact.fonction')"
                :rules="[obligatoireRule(), serveurRule('fonction')]"
                hide-details="auto"
              >
                <template #selection="{item}">
                  {{ item.libelle }}
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-form>
    </v-card>

    <demandeAction />
  </div>
</template>

<script>
import moment from 'moment';
import DemandeEntete from '@/components/workflow/DemandeEntete';
import DemandeAction from '@/components/workflow/DemandeAction';
import EditeurDemande from '@/plugins/mixins/EditeurDemande';
import { routeEnter } from '@/utils/entite';
import toasted from '@/utils/toasted';
import ServeurRule from '@/plugins/mixins/ServeurRule';
import ValidationRules from '@/plugins/mixins/ValidationRules';

export default {
  components: {
    demandeEntete: DemandeEntete,
    demandeAction: DemandeAction,
  },
  mixins: [ServeurRule, ValidationRules, EditeurDemande],
  beforeRouteEnter: routeEnter(withId => {
    withId(
      id => `workflow/demande/${id}`,
      (vm, body) => {
        vm.initForm(body);
      },
    );
  }),
  data() {
    return {
      dsType: null,
      valid: true,

      enums: {
        civilites: [],
        fonctions: [],
      },

      motifRefus: null,

      form: {
        origineDemande: null,

        organisme: null,
        nomUsuel: null,
        raisonSociale: null,
        pmId: null,

        civilite: {
          id: null,
          libelle: null,
        },
        nom: null,
        prenoms: null,
        mailPrincipal: null,
        telephone: null,

        contact: {
          fonction: {
            id: null,
            libelle: null,
          },
          personneMorale: {
            id: null,
            libelle: null,
          },
          telephones: [],
          adresses: [],
          principal: true,
        },
        contacts: [],

        // Collections
        adresses: null,
      },
    };
  },

  methods: {
    initForm(data) {
      this.initEditeurDemande(data);

      //injection du droit d'edition
      this.control.edition = 'AUCUN';
      this.control.complementsPossible = false;

      //chargement du diff
      this.typeDemande = data.demandeType.libelle;
      this.demandeur = data.demandeurCreation;
      this.commentaire = data.commentaire;
      this.dateCreation = moment(data.dateCreation).format(
        'DD/MM/YYYY [à] HH:mm',
      );

      const loaded = data.objet;

      this.form.origineDemande = loaded.origineDemande;
      this.form.organisme = loaded.organisme;
      this.form.adresses = [...loaded.adresses];
      this.form.civilite = loaded.civilite;
      this.form.nom = loaded.nom;
      this.form.prenoms = loaded.prenoms;
      this.form.mailPrincipal = loaded.mailPrincipal;
      this.form.telephone = loaded.telephone;
      this.form.contact.fonction = loaded.fonction;
      this.form.fonction = loaded.fonction;

      if (data.enums) {
        this.initEnums(data.enums);
      }

      this.__initEdition();
    },
    getForms() {
      return [this.$refs.form];
    },
    valider() {
      if (this.form.pmId === null) {
        return toasted.error('Personne morale obligatoire');
      }
      return this.getForms()
        .map(f => f.validate())
        .every(v => !!v);
    },
    validerDemande() {
      // Création d'une demande de création de contact avec la PM ajoutée ou sélectionnée
      const id = this.idDemande;
      var data = this.form;
      if (!this.valider()) {
        this.control.dialogValider = false;
        return;
      }
      data.commentaire = this.commentaire;
      var url = `workflow/demande/inscription/${id}/valider`;
      this.control.validerDemande = true;
      this.$api(url)
        .post(data)
        .then(response => {
          toasted.info('demande.status.valide');
          this.__annuler(false);
          this.$router.push({
            name: 'workflow/demande/personne-physique/detail',
            params: {
              id: response.body.id,
            },
          }); //vers la demande de création de contact
        })
        .catch(errors => {
          this.control.dialogValider = false;
          this.errorsHandler(errors);
        })
        .finally(() => {
          this.control.validerDemande = false;
        });
    },

    initEnums(body) {
      const enums = this.enums;
      enums.civilites = body.civilites;
      enums.fonctions = body.fonctions;
    },
    selectPM() {
      this.$refs.dialogRecherchePM.show();
    },
    createPM() {
      this.$refs.dialogCreationPM.show();
    },
    addPM(personneMorale) {
      const dateLimiteValidite = moment()
        .add(2, 'years')
        .format('YYYY-MM-DD');
      this.form.nomUsuel = personneMorale.nomUsuel;
      this.form.raisonSociale = personneMorale.raisonSociale;
      this.form.pmId = personneMorale.id;
      this.form.contact.personneMorale.id = this.form.pmId;
      this.form.contact.personneMorale.libelle = this.form.nomUsuel;
      this.form.contact.adresses = this.form.adresses;
      this.form.contact.dateLimiteValidite = dateLimiteValidite;
      this.form.contacts.push(this.form.contact);
    },
  },
  computed: {
    listeCivilites() {
      if (this.enums.civilites.length) {
        return this.enums.civilites;
      } else {
        const civilites = [];
        if (this.form && this.form.civilite) {
          civilites.push(this.form.civilite);
        }
        return civilites;
      }
    },
    listeFonctions() {
      if (this.enums.fonctions.length) {
        return this.enums.fonctions;
      } else {
        const fonctions = [];
        if (this.form && this.form.contact.fonction) {
          fonctions.push(this.form.contact.fonction);
        }
        return fonctions;
      }
    },
  },
};
</script>
